import { Drawer } from '@theme/drawer';
import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent } from '@theme/events';

/**
 * @typedef {object} FilterDrawerRefs
 * @property {HTMLDialogElement} drawer
 * @property {HTMLFormElement} form
 * @property {HTMLFormElement[]} [dialogForms]
 */

/**
 * @extends {Drawer}
 */
class FilterDrawer extends Drawer {
  static drawerName = 'filter-drawer';

  sectionId = this.dataset.sectionId;

  /** @type {symbol | null} */
  #pendingRequest = null;

  /** @type {(event: Event) => void} */
  #onFormChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const form = this.refs.form;
    if (target.type === 'checkbox' || target.tagName === 'SELECT') {
      if (form instanceof HTMLFormElement) {
        this.#submitForm(form);
      }
    }
  };

  /** @type {(event: Event) => void} */
  #onFormSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;
    this.#submitForm(form);
  };

  #onPopState = () => {
    this.#renderFromURL(new URL(window.location.href), { updateHistory: false });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#registerEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unregisterEvents();
  }

  /**
   * @param {{ href?: string, reopen?: boolean }} data
   * @param {Event} [event]
   */
  handleNavigate(data, event) {
    event?.preventDefault();
    if (!data || !data.href) return;
    const reopen = typeof data.reopen === 'boolean' ? data.reopen : undefined;
    this.#renderFromURL(new URL(data.href, window.location.origin), { reopen });
  }

  /**
   * @param {Event} [event]
   */
  applyAndClose(event) {
    event?.preventDefault();
    const form = this.refs.form;
    if (!(form instanceof HTMLFormElement)) return;
    this.#submitForm(form, { reopen: false });
  }

  /** @param {Event} event */
  handleSortChange(event) {
    const form = this.refs.form;
    if (!(event.target instanceof HTMLSelectElement) || !(form instanceof HTMLFormElement)) return;
    const select = event.target;

    /** @type {HTMLSelectElement | null} */
    const existing = form.querySelector('select[name="sort_by"]');
    if (existing && existing !== select) {
      existing.value = select.value;
    }

    this.#submitForm(form);
  }

  #registerEvents() {
    const form = this.refs.form;
    if (form instanceof HTMLFormElement) {
      form.addEventListener('change', this.#onFormChange);
      form.addEventListener('submit', this.#onFormSubmit);
    }
    window.addEventListener('popstate', this.#onPopState);
  }

  #unregisterEvents() {
    const form = this.refs.form;
    if (form instanceof HTMLFormElement) {
      form.removeEventListener('change', this.#onFormChange);
      form.removeEventListener('submit', this.#onFormSubmit);
    }
    window.removeEventListener('popstate', this.#onPopState);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {{ reopen?: boolean }} [options]
   */
  async #submitForm(form, options = {}) {
    if (!(form instanceof HTMLFormElement)) return;
    const params = new URLSearchParams();
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
      if (key === 'page') continue;
      if (typeof value !== 'string') continue;
      if (value.trim() === '') continue;
      params.append(key, value);
    }

    if (options.reopen === false && this.refs.drawer?.hasAttribute('open')) {
      await this.#waitForClose();
    }

    const url = new URL(window.location.href);
    url.search = params.toString();
    await this.#renderFromURL(url, { reopen: options.reopen });
  }

  /**
   * @param {URL} url
   * @param {{ updateHistory?: boolean, reopen?: boolean }} [options]
   */
  async #renderFromURL(url, { updateHistory = true, reopen } = {}) {
    if (!this.sectionId) return;

    const reopenDrawer = typeof reopen === 'boolean' ? reopen : Boolean(this.refs.drawer?.open);
    this.#setLoading(true);
    const requestToken = Symbol('filter-drawer-request');
    this.#pendingRequest = requestToken;

    try {
      await sectionRenderer.renderSection(this.sectionId, { url });
      if (this.#pendingRequest === requestToken) {
        const cleanUrl = new URL(url);
        cleanUrl.searchParams.delete('section_id');
        if (updateHistory) {
          window.history.pushState({}, '', cleanUrl);
        }
        const cleanSearchParams = new URLSearchParams(cleanUrl.search);
        document.dispatchEvent(new FilterUpdateEvent(cleanSearchParams));
        if (reopenDrawer) {
          requestAnimationFrame(() => {
            if (this.refs.drawer && !this.refs.drawer.open) {
              this.refs.drawer.showModal();
            }
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (this.#pendingRequest === requestToken) {
        this.#pendingRequest = null;
      }
      this.#setLoading(false);
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    const isBusy = Boolean(isLoading);
    if (section) {
      section.toggleAttribute('data-loading', isBusy);
    }
    this.toggleAttribute('data-loading', isBusy);
  }

  async #waitForClose() {
    if (!this.refs.drawer?.hasAttribute('open')) return;
    /** @type {Promise<void>} */
    const promise = new Promise((resolve) => {
      const dialog = this.refs.drawer;
      const handle = () => {
        dialog.removeEventListener('close', handle);
        resolve();
      };
      dialog.addEventListener('close', handle, { once: true });
      this.closeDrawer();
    });
    await promise;
  }
}

if (!customElements.get('filter-drawer')) {
  customElements.define('filter-drawer', FilterDrawer);
}
